import React from 'react';
import { Space, Tag, Avatar, Tooltip } from 'antd';
import { getOrganSystemInfo, ORGAN_SYSTEM_COLORS } from '../utils/organSystemConstants';

const OrganSystemTags = ({ 
  organSystems, 
  size = 'default', 
  showIcons = true, 
  showNames = true,
  maxDisplay = null,
  style = {}
}) => {
  if (!organSystems || !Array.isArray(organSystems) || organSystems.length === 0) {
    return <span style={{ color: '#999' }}>No organ systems</span>;
  }

  const displaySystems = maxDisplay ? organSystems.slice(0, maxDisplay) : organSystems;
  const remainingCount = maxDisplay && organSystems.length > maxDisplay ? organSystems.length - maxDisplay : 0;

  return (
    <Space wrap size="small" style={style}>
      {displaySystems.map(systemKey => {
        const systemInfo = getOrganSystemInfo(systemKey);
        if (!systemInfo) return null;
        
        const tagContent = (
          <Space size="small">
            {showIcons && (
              <Avatar
                src={systemInfo.iconPath}
                size={size === 'small' ? 24 : 28}
                style={{
                  backgroundColor: ORGAN_SYSTEM_COLORS[systemKey],
                  width: size === 'small' ? '24px' : '28px',
                  height: size === 'small' ? '24px' : '28px',
                  minWidth: size === 'small' ? '24px' : '28px'
                }}
              />
            )}
            {showNames && <span>{systemInfo.name}</span>}
          </Space>
        );

        const tooltipTitle = showNames ? systemInfo.description : `${systemInfo.name} - ${systemInfo.description}`;

        return (
          <Tooltip key={systemKey} title={tooltipTitle}>
            <Tag
              color={ORGAN_SYSTEM_COLORS[systemKey]}
              className={
                !showNames ? 'organ-system-icon-only' :
                size === 'small' ? 'organ-system-icon-small' : 'organ-system-icon-large'
              }
              style={{
                margin: '2px',
                cursor: 'help',
                display: 'inline-flex',
                alignItems: 'center',
                padding: !showNames ? '4px 6px' : '4px 8px'
              }}
            >
              {tagContent}
            </Tag>
          </Tooltip>
        );
      })}
      
      {remainingCount > 0 && (
        <Tooltip title={`${remainingCount} more organ systems`}>
          <Tag style={{ margin: '2px', cursor: 'help' }}>
            +{remainingCount}
          </Tag>
        </Tooltip>
      )}
    </Space>
  );
};

// Icon-only version for table displays (larger icons, no text)
export const IconOnlyOrganSystemTags = ({
  organSystems,
  maxDisplay = 4,
  size = "small",
  showMore = true,
  maxRows = 1,
  iconsPerRow = 4
}) => {
  if (!organSystems || !Array.isArray(organSystems) || organSystems.length === 0) {
    return <span style={{ color: '#999' }}>No organ systems</span>;
  }

  // If showMore is false, limit to maxRows * iconsPerRow
  const totalMaxDisplay = showMore ? maxDisplay : Math.min(maxRows * iconsPerRow, organSystems.length);
  const displaySystems = organSystems.slice(0, totalMaxDisplay);

  // Create rows for grid layout
  const rows = [];
  for (let i = 0; i < displaySystems.length; i += iconsPerRow) {
    rows.push(displaySystems.slice(i, i + iconsPerRow));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {row.map(systemKey => {
            const systemInfo = getOrganSystemInfo(systemKey);
            if (!systemInfo) return null;

            return (
              <Tooltip key={systemKey} title={`${systemInfo.name} - ${systemInfo.description}`}>
                <Avatar
                  src={systemInfo.iconPath}
                  size={size === 'small' ? 24 : 28}
                  style={{
                    backgroundColor: ORGAN_SYSTEM_COLORS[systemKey],
                    width: size === 'small' ? '24px' : '28px',
                    height: size === 'small' ? '24px' : '28px',
                    minWidth: size === 'small' ? '24px' : '28px',
                    cursor: 'help',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  {systemInfo.name.charAt(0)}
                </Avatar>
              </Tooltip>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Compact version for small spaces
export const CompactOrganSystemTags = ({ organSystems, maxDisplay = 2 }) => (
  <OrganSystemTags
    organSystems={organSystems}
    size="small"
    showNames={false}
    maxDisplay={maxDisplay}
  />
);

// Full version with names and icons
export const FullOrganSystemTags = ({ organSystems }) => (
  <OrganSystemTags
    organSystems={organSystems}
    showIcons={true}
    showNames={true}
  />
);

export default OrganSystemTags;
